import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function RateLawCalculator() {
  const [rateConstant, setRateConstant] = useState("");
  const [concentrationA, setConcentrationA] = useState("");
  const [orderA, setOrderA] = useState("1");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Rate Law Calculator", url: "https://calclab.app/calculator/rate-law" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Rate Law Calculator",
      "Calculate reaction rate from rate law. Free online chemistry calculator with instant results and detailed explanations.",
      "https://calclab.app/calculator/rate-law",
      lang
    )
  ]);

  const calculate = () => {
    const k = parseFloat(rateConstant);
    const a = parseFloat(concentrationA);
    const m = parseFloat(orderA);

    if (isNaN(k) || isNaN(a) || isNaN(m) || k < 0 || a < 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    // Rate = k[A]^m
    const rate = k * Math.pow(a, m);
    setResult(rate);

    toast({
      title: "Calculation Complete",
      description: `Rate = ${rate.toExponential(2)} M/s`,
    });
  };

  const reset = () => {
    setRateConstant("");
    setConcentrationA("");
    setOrderA("1");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Rate Law Calculator" 
      calculatorPath="/calculator/rate-law"
      category="chemistry"
      subcategory="Kinetics"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Rate Law Calculator</CardTitle>
            <CardDescription>
              Calculate reaction rate from rate law
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="k">Rate Constant (k)</Label>
                <Input
                  id="k"
                  type="number"
                  step="any"
                  value={rateConstant}
                  onChange={(e) => setRateConstant(e.target.value)}
                  placeholder="Enter rate constant"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="conc">[A] Concentration (M)</Label>
                <Input
                  id="conc"
                  type="number"
                  step="any"
                  value={concentrationA}
                  onChange={(e) => setConcentrationA(e.target.value)}
                  placeholder="Enter concentration"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Reaction Order (m)</Label>
                <Input
                  id="order"
                  type="number"
                  step="1"
                  value={orderA}
                  onChange={(e) => setOrderA(e.target.value)}
                  placeholder="0, 1, or 2"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Reaction Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toExponential(2)} M/s</p>
                    <CopyResultButton value={`${result.toExponential(2)} M/s`} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Rate Law</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">Rate = k[A]^m</p>
                <p className="text-sm text-muted-foreground mt-2">
                  where k = rate constant, m = reaction order
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
